export default {
  howItWorksPage: {
    seoTitle: 'Cómo funciona Geofleet — Del pedido a la prueba de entrega | Geofleet',
    seoDescription:
      'Descubre cómo funciona Geofleet en seis pasos: importa pedidos, planifica rutas optimizadas, despacha a la app del conductor, sigue todo en vivo, envía a tus clientes un enlace de seguimiento y registra la prueba de entrega, todo desde un solo centro de comando con un Copilot de IA.',
    heroBadge: 'Cómo funciona',
    heroTitle: 'Cómo funciona Geofleet:',
    heroTitleHighlight: 'entra el pedido, sale la prueba de entrega',
    /** Direct answer paragraph — written to be quotable by search and AI answer engines. */
    heroLead:
      'Geofleet es un software de gestión de entregas. Importas los pedidos desde un CSV, tu tienda o una API. Geofleet planifica rutas optimizadas y las envía a los teléfonos de los conductores. A partir de ahí sigues cada parada en vivo, envías a los clientes un enlace de seguimiento por mensaje y registras la prueba de entrega con foto, firma u OTP. Todo funciona desde un solo centro de comando, y un Copilot de IA responde preguntas sobre esos mismos datos en vivo.',
    heroFacts: [
      { icon: 'ri-price-tag-3-line', label: 'Desde $100/mes para 10 conductores' },
      { icon: 'ri-smartphone-line', label: 'La app del conductor funciona sin conexión' },
      { icon: 'ri-link', label: 'Los clientes no instalan ninguna app' },
    ],
    stepsKicker: 'El ciclo de la entrega',
    stepsTitle: 'Seis pasos del pedido a la prueba de entrega',
    stepsIntro: 'Cada pedido pasa por los mismos seis pasos en Geofleet, tanto si tienes 5 conductores como 500.',
    stepLabel: 'Paso {{n}}',
    steps: [
      {
        title: 'Importa tus pedidos',
        body: 'Sube un CSV o una hoja de cálculo, sincroniza pedidos automáticamente desde Shopify y otras plataformas OMS, envíalos por la API o MCP, o añádelos a mano. Las direcciones se geocodifican al importarlas para que estén listas para planificar.',
        points: ['Plantillas de CSV y hojas de cálculo', 'Shopify, Unicommerce y más de 10 integraciones OMS', 'REST API y MCP para tus propios sistemas'],
        linkLabel: 'Ver integraciones',
        linkHref: '/integrations',
        imageAlt: 'Subida de un CSV de pedidos de entrega a Geofleet',
      },
      {
        title: 'Planifica rutas optimizadas',
        body: 'Elige un perfil de planificación, como «Entregas de mañana», y genera un plan inteligente. El motor de rutas tiene en cuenta ventanas horarias, capacidad del vehículo, tráfico y carga de trabajo de los conductores, y agrupa las paradas en viajes.',
        points: ['Ventanas horarias y límites de turno', 'Capacidad por peso, volumen y artículos', 'Carga de trabajo equilibrada entre conductores'],
        linkLabel: 'Planificador de rutas',
        linkHref: '/features/route-planner',
        imageAlt: 'Smart Planning de Geofleet generando rutas optimizadas a partir de un perfil de planificación',
      },
      {
        title: 'Despacha a los conductores',
        body: 'Revisa la distancia, la duración y el número de pedidos de cada viaje y publícalo. La ruta llega a la app del conductor con navegación paso a paso. En Premium, el despacho y la asignación automáticos con IA asignan los viajes por ti.',
        points: ['Publicación en un clic a la app del conductor', 'Funciona sin conexión y se sincroniza al reconectar', 'Despacho automático con IA en Premium'],
        linkLabel: 'App del conductor',
        linkHref: '/features/driver-app',
        imageAlt: 'Un viaje publicado despachándose a un conductor en Geofleet',
      },
      {
        title: 'Sigue en vivo y ajusta',
        body: 'Todos los vehículos y paradas están en un solo mapa en vivo. Las paradas retrasadas o en riesgo suben al principio de la cola. Puedes reordenar paradas, reasignar pedidos o añadir una parada a mitad de ruta, y la app del conductor se actualiza al instante.',
        points: ['Mapa en vivo con ruta planificada frente a real', 'Excepciones y riesgo de SLA primero', 'Reordenación con arrastrar y soltar'],
        linkLabel: 'Informes y analítica',
        linkHref: '/features/reports-analytics',
        imageAlt: 'Reordenación de pedidos de entrega en un mapa de rutas en vivo en Geofleet',
      },
      {
        title: 'Mantén informados a los clientes',
        body: 'Los clientes reciben un enlace de seguimiento por SMS, email o WhatsApp. Se abre en cualquier navegador y muestra el estado en vivo, la ETA y al conductor en un mapa, así que no necesitan instalar nada. En Premium, la página lleva tu propia marca.',
        points: ['Actualizaciones automáticas en los momentos clave', 'ETA en vivo y contacto con el conductor', 'Página de seguimiento con tu marca en Premium'],
        linkLabel: 'Seguimiento con tu marca',
        linkHref: '/features/branded-tracking',
        imageAlt: 'Un cliente siguiendo una entrega en vivo con un enlace de seguimiento de Geofleet en su teléfono',
      },
      {
        title: 'Demuestra la entrega y mide',
        body: 'Los conductores registran una foto, firma electrónica, OTP o escaneo de código de barras en la puerta. Cada prueba lleva fecha, hora y geolocalización. Los resultados alimentan la tasa de puntualidad, el coste por entrega y las fichas de conductores, para que veas qué mejorar mañana.',
        points: ['Foto, firma, OTP y código de barras', 'Registros de prueba consultables y exportables', 'KPIs de puntualidad, coste por entrega y conductores'],
        linkLabel: 'Prueba de entrega',
        linkHref: '/features/proof-of-delivery',
        imageAlt: 'Conductor registrando una foto como prueba de entrega en la app del conductor de Geofleet',
      },
    ],
    surfacesKicker: 'Dónde lo gestionas',
    surfacesTitle: 'Un solo centro de comando, con Copilot integrado',
    surfacesIntro:
      'Los seis pasos ocurren en un solo lugar. El centro de comando es la vista en vivo de tu operación. Copilot es un asistente de IA que lee los mismos datos y puede actuar sobre ellos dentro de tus permisos.',
    towerSectionTitle: 'Centro de comando',
    towerLead: 'Pedidos, conductores, el mapa en vivo, ETAs y excepciones en una sola pantalla, para que la siguiente acción sea evidente.',
    towerFeatureCards: [
      {
        title: 'Trae tus datos a tu manera',
        description: 'Integraciones, MCP, plantillas CSV o entrada manual, todo en la misma lista de pedidos.',
      },
      {
        title: 'Planifica, asigna y adapta',
        description: 'Reasigna, reordena, inserta paradas y haz actualizaciones masivas dentro de los límites de roles y políticas.',
      },
      {
        title: 'Un mapa, colas priorizadas',
        description: 'Las excepciones suben arriba y cada rol ve su parte: despacho, líderes de hub o socios.',
      },
      {
        title: 'Mide y audita',
        description: 'Tasa de puntualidad, coste por entrega y fichas de rendimiento, con un registro completo de quién cambió qué y cuándo.',
      },
    ],
    copilotSectionTitle: 'Copilot',
    copilotLead: 'Pregunta sobre tu operación en lenguaje natural. Las respuestas salen de datos en vivo, no de texto genérico de IA.',
    copilotFeatureCards: [
      {
        title: 'Pregunta en lenguaje natural',
        description: '«¿Qué paradas de la zona norte van con retraso?» o «¿Qué ha cambiado desde las 09:00?»',
      },
      {
        title: 'Respuestas operativas',
        description: 'Listas filtradas, comparaciones y resúmenes para despacho, rutas y excepciones.',
      },
      {
        title: 'Acciones sugeridas, con límites',
        description: 'Copilot propone cambios de ruta o actualizaciones masivas para que los confirmes. No puede hacer nada que tu rol no permita.',
      },
      {
        title: 'Onboarding más rápido',
        description: 'Indicaciones guiadas para configurar hubs, integraciones y flujos de trabajo sin malabares con hojas de cálculo.',
      },
    ],
    faqKicker: 'Preguntas frecuentes',
    faqTitle: 'Preguntas sobre cómo funciona Geofleet',
    faqs: [
      {
        question: '¿Cómo funciona Geofleet?',
        answer:
          'Geofleet recibe pedidos de entrega desde un CSV, tu tienda o una API y planifica rutas optimizadas. Despacha esas rutas a una app del conductor, sigue cada parada en vivo y envía a los clientes un enlace de seguimiento. En la puerta, los conductores registran la prueba de entrega con foto, firma u OTP. Los despachadores gestionan todo desde un solo centro de comando, con un Copilot de IA para preguntas y acciones masivas.',
      },
      {
        question: '¿Cómo llevo mis pedidos a Geofleet?',
        answer:
          'Hay cuatro formas. Puedes subir un CSV o una hoja de cálculo, conectar un OMS o tienda como Shopify o Unicommerce para que los pedidos se sincronicen automáticamente, enviar pedidos por la REST API o MCP, o añadirlos manualmente. Puedes combinar métodos: muchos equipos empiezan con CSV y añaden una integración después.',
      },
      {
        question: '¿Los conductores tienen que instalar una app?',
        answer:
          'Sí. Los conductores usan la app del conductor de Geofleet para recibir rutas, navegar, registrar la prueba de entrega y marcar asistencia. La app funciona sin conexión y se sincroniza cuando el teléfono vuelve a conectarse.',
      },
      {
        question: '¿Los clientes tienen que instalar algo para seguir una entrega?',
        answer:
          'No. Los clientes reciben un enlace de seguimiento por SMS, email o WhatsApp que se abre en cualquier navegador. Muestra el estado en vivo, la ETA y la ubicación del conductor.',
      },
      {
        question: '¿Puede Copilot cambiar pedidos o rutas por su cuenta?',
        answer:
          'Solo dentro de los permisos que definas. Copilot respeta el acceso basado en roles, así que solo puede hacer lo que el usuario conectado tiene permitido. Propone acciones como cambios de ruta o actualizaciones masivas para que las confirmes.',
      },
      {
        question: '¿Cuánto cuesta probar Geofleet?',
        answer:
          'Los planes empiezan con Geofleet Lite por $49 al mes para 3 conductores y 2.000 pedidos, más $10 por cada conductor adicional. Professional cuesta $19 y Premium $29 por conductor al mes, con facturación trimestral. Professional incluye una prueba gratuita de 14 días.',
      },
    ],
    visualHeroAlt: 'Centro de comando de Geofleet con mapa en vivo, KPIs y lista de pedidos',
    visualCommandAlt: 'Centro de comando de Geofleet — mapa en vivo y vista operativa',
    visualCopilotAlt: 'Copilot de Geofleet respondiendo una pregunta de despacho junto a una ruta en vivo',
    closingTitle: 'Mira cómo avanzan tus propios pedidos',
    closingSub: 'Empieza con un CSV de pedidos reales, o reserva una demo y repasaremos tu flujo de trabajo contigo.',
  },
};
